import type { FC } from "react";
import { useParams } from "react-router";
import { useFetchedResource } from "../../api/APIContext";
import { useApiCall } from "../../api/api";
import type { Checklist } from "../../api/dto/Checklist";
import { CardFlowEditor } from "../../components/cardFlowEditor/CardFlowEditor";
import type { CardApi } from "../../components/cardFlowEditor/cardApi";
import { Loading } from "../../components/Loading";
import { useRefreshEffect } from "../../components/RefreshController";
import { useChecklistPermissions } from "../../permissions/checklistPolicy";
import { ForbiddenPage } from "../ForbiddenPage";
import { ContentContainer } from "../../components/content/ContentContainer";
import { PageHeader } from "../../components/content/PageHeader";

// Matches Domain\Checklists\ChecklistCardType (gateway-api): the design the app shows the card in.
const cardTypeOptions: Record<string, string> = {
  intro: "Introductie (blauw)",
  standard: "Standaard",
  action_required: "Actie vereist",
};

// Thin domain binding: fetches the checklist, gates on permissions, and wires the generic
// CardFlowEditor to the checklist-card endpoints. The editor itself (columns, cards, drag, actions,
// language switching) lives in src/components/cardFlowEditor — deliberately not page-specific, so
// SettingsLiftingCategoryEditorPage renders the exact same component with its own `api` binding
// instead of duplicating any of this.
export const SettingsChecklistEditorPage: FC = () => {
  const { checklistId } = useParams();
  const { getChecklist, updateChecklistCard, addChecklistCard, deleteChecklistCard, uploadChecklistCardImage } =
    useApiCall();
  const checklistResource = useFetchedResource<Checklist, string>((id) => getChecklist(id));
  const checklist = checklistResource.resource;
  const { loading: permissionsLoading, canAccessChecklistSettings } = useChecklistPermissions();

  useRefreshEffect(() => {
    if (!checklistId) return;
    checklistResource.reload(checklistId);
  }, [checklistId]);

  const api: CardApi | null = checklist
    ? {
        updateCard: (card) => updateChecklistCard(checklist.uuid, card),
        createCard: (name, cardType, column, positionInColumn) =>
          addChecklistCard(checklist.uuid, name, cardType, column, positionInColumn),
        deleteCard: (uuid) => deleteChecklistCard(uuid),
        uploadCardImage: (file) => uploadChecklistCardImage(file),
      }
    : null;

  return (
    <Loading loading={permissionsLoading}>
      {!canAccessChecklistSettings() ? (
        <ForbiddenPage />
      ) : (
        <>
          <ContentContainer size={"xl"}>
            <PageHeader>
              {checklist ? `${checklist.materialType} (${checklist.liftingCategoryLabel})` : "Checklist"}
            </PageHeader>
          </ContentContainer>
          <Loading loading={checklistResource.loading}>
            {checklist && api ? (
              <div className={"h-[calc(100vh-12rem)] border-t border-slate-200"}>
                <CardFlowEditor
                  cards={checklist.cards}
                  stepOffset={checklist.stepOffset}
                  api={api}
                  cardTypeOptions={cardTypeOptions}
                />
              </div>
            ) : null}
          </Loading>
        </>
      )}
    </Loading>
  );
};
